import React from 'react';
import {view, Icon, Link} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  LightCard,
  ThemeProvider,
  LinkTheme,
} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {business} from '@sail/icons/react/Icon';

export type ValueProps = {
  merchant_name: string;
  merchant_avatar: string;
  merchant_website: string;
  merchant_email: string;
  merchant_phone: string;
  audience_type: string;
};

const messages = defineMessages({
  websiteTitle: {
    id: 'mse_inline_data.universal_help_business_detail.website_title',
    description: 'Heading for business website section',
    defaultMessage: 'Website',
  },
  emailTitle: {
    id: 'mse_inline_data.universal_help_business_detail.email_title',
    description: 'Heading for business email section',
    defaultMessage: 'Email',
  },
  phoneNumberTitle: {
    id: 'mse_inline_data.universal_help_business_detail.phone_number_title',
    description: 'Heading for business phone number section',
    defaultMessage: 'Phone number',
  },
  noAdditionalBusinessDetails: {
    id: 'mse_inline_data.universal_help_business_detail.no_additional_business_details',
    description:
      'Message directing user to contact the business when no additional details available.',
    defaultMessage:
      'We do not have additional details of {merchant}, you will need to reach out to the business directly for help.',
  },
});

const MerchantWebsiteComponent = ({
  merchantWebsite,
  isStripeAudience,
  hasPhoneOrEmail,
}: {
  merchantWebsite: string;
  isStripeAudience: boolean;
  hasPhoneOrEmail: boolean;
}) => {
  return (
    <view.div
      data-testid="merchant-website"
      css={{
        font: 'body.medium',
        marginBottom: hasPhoneOrEmail ? 'medium' : '0',
      }}
    >
      <Link
        type="primary"
        href={merchantWebsite}
        target="_blank"
        rel="noopener noreferrer"
        css={{
          color: isStripeAudience ? '#7A73FF' : 'rgb(0, 102, 53)',
          fontWeight: 'bold',
        }}
      >
        {merchantWebsite}
      </Link>
    </view.div>
  );
};

type MerchantContactInfoComponentProps = {
  type: 'email' | 'phone';
  value: string;
  isStripeAudience: boolean;
  css?: React.CSSProperties;
};

const MerchantContactInfoComponent = ({
  type,
  value,
  isStripeAudience,
  css,
}: MerchantContactInfoComponentProps) => {
  const titleMessage =
    type === 'email' ? messages.emailTitle : messages.phoneNumberTitle;
  const href = type === 'email' ? `mailto:${value}` : `tel:${value}`;
  const id = `merchant-${type}`;
  return (
    <view.div css={{stack: 'y', gap: 'xsmall'}}>
      <view.div css={{font: 'label.medium.emphasized'}}>
        <FormattedMessage {...titleMessage} />
      </view.div>
      <view.div data-testid={id} css={{...css, font: 'label.medium'}}>
        <Link
          type="primary"
          href={href}
          css={{
            color: isStripeAudience ? '#596171' : 'rgb(0, 102, 53)',
            fontWeight: isStripeAudience ? 'normal' : 'bold',
          }}
        >
          {value}
        </Link>
      </view.div>
    </view.div>
  );
};

const LinkBusinessDetails = ({
  merchantPropsValue,
}: {
  merchantPropsValue: ValueProps;
}) => {
  const analytics = useAnalytics();
  const {
    merchant_email: merchantEmail,
    merchant_phone: merchantPhone,
    merchant_website: merchantWebsite,
    audience_type: audienceType,
  } = merchantPropsValue;

  const isStripeAudience = audienceType === 'stripe';
  const hasPhoneOrEmail = merchantPhone || merchantEmail;

  analytics.viewed('viewed_link_business_card_detail');
  return (
    <view.div>
      {!isStripeAudience && merchantWebsite && (
        <>
          <view.div css={{font: 'label.medium.emphasized'}}>
            <FormattedMessage {...messages.websiteTitle} />
          </view.div>
          <MerchantWebsiteComponent
            merchantWebsite={merchantWebsite}
            isStripeAudience={isStripeAudience}
            hasPhoneOrEmail={!!hasPhoneOrEmail}
          />
        </>
      )}

      {hasPhoneOrEmail &&
        (isStripeAudience ? (
          <view.div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'large',
            }}
          >
            {merchantPhone && (
              <>
                <view.div css={{flex: '1 1 auto'}}>
                  <MerchantContactInfoComponent
                    type="phone"
                    value={merchantPhone}
                    isStripeAudience={isStripeAudience}
                  />
                </view.div>
              </>
            )}
            {merchantEmail && (
              <>
                <view.div css={{flex: '1 1 auto'}}>
                  <MerchantContactInfoComponent
                    type="email"
                    value={merchantEmail}
                    isStripeAudience={isStripeAudience}
                  />
                </view.div>
              </>
            )}
          </view.div>
        ) : (
          <>
            {merchantEmail && (
              <>
                <MerchantContactInfoComponent
                  type="email"
                  value={merchantEmail}
                  isStripeAudience={isStripeAudience}
                  css={{marginBottom: hasPhoneOrEmail ? 'medium' : '0'}}
                />
              </>
            )}
            {merchantPhone && (
              <>
                <MerchantContactInfoComponent
                  type="phone"
                  value={merchantPhone}
                  isStripeAudience={isStripeAudience}
                  css={{marginBottom: 'medium'}}
                />
              </>
            )}
          </>
        ))}
    </view.div>
  );
};

const LinkBusinessCardDetail = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const merchantPropsValue = datum.value as ValueProps;
  const {
    merchant_name: merchantName,
    merchant_avatar: merchantAvatar,
    merchant_website: merchantWebsite,
    merchant_phone: merchantPhone,
    merchant_email: merchantEmail,
    audience_type: audienceType,
  } = merchantPropsValue;

  const isStripeAudience = audienceType === 'stripe';

  const hasContactInfo = merchantWebsite || merchantPhone || merchantEmail;
  const hasPhoneOrEmail = merchantPhone || merchantEmail;

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <LightCard isOuter>
        <view.div css={{stack: 'y', gap: 'large'}}>
          <view.div css={{stack: 'y', gap: 'xsmall'}}>
            <view.div css={{stack: 'x', gap: 'small'}}>
              {isStripeAudience ? (
                merchantAvatar && (
                  <view.div
                    css={{
                      stack: 'x',
                      width: 'large',
                      height: 'large',
                      alignSelfY: 'center',
                    }}
                  >
                    <view.img
                      data-testid="merchant-avatar"
                      src={merchantAvatar}
                      css={{
                        width: 'large',
                        height: 'large',
                        borderRadius: '50%',
                        border: '1px inset rgba(0, 0, 0, 0.15)',
                      }}
                    />
                  </view.div>
                )
              ) : (
                <view.div
                  css={{
                    stack: 'x',
                    width: 'large',
                    height: 'large',
                    alignSelfY: 'center',
                  }}
                >
                  <view.div
                    css={{
                      borderRadius: '50%',
                      border: '1px inset rgba(0, 0, 0, 0.15)',
                      width: 'fill',
                      height: 'fill',
                    }}
                  >
                    {merchantAvatar ? (
                      <view.img
                        data-testid="merchant-avatar"
                        src={merchantAvatar}
                        css={{
                          width: 'large',
                          height: 'large',
                          borderRadius: '50%',
                          border: '1px inset rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    ) : (
                      <Icon
                        data-testid="merchant-avatar"
                        icon={business}
                        css={{
                          padding: 'xsmall',
                          width: 'fill',
                          height: 'fill',
                        }}
                      />
                    )}
                  </view.div>
                </view.div>
              )}

              <view.h3
                css={{margin: 0, font: 'heading.large'}}
                data-testid="merchant-name"
              >
                {merchantName}
              </view.h3>
            </view.div>

            {isStripeAudience && merchantWebsite && (
              <>
                <MerchantWebsiteComponent
                  merchantWebsite={merchantWebsite}
                  isStripeAudience={isStripeAudience}
                  hasPhoneOrEmail={!!hasPhoneOrEmail}
                />
                <LinkBusinessDetails merchantPropsValue={merchantPropsValue} />
              </>
            )}
          </view.div>

          {!isStripeAudience && hasContactInfo && (
            <>
              <LinkBusinessDetails merchantPropsValue={merchantPropsValue} />
            </>
          )}

          {!hasContactInfo && (
            <>
              <view.div css={{gap: 'medium', stack: 'y'}}>
                <view.div data-testid="no-additional-merchant-details">
                  <FormattedMessage
                    {...messages.noAdditionalBusinessDetails}
                    values={{merchant: merchantName}}
                  />
                </view.div>
              </view.div>
            </>
          )}
        </view.div>
      </LightCard>
    </view.div>
  );
};

export const LinkBusinessCard = (props: InlineDatumReactComponentProps) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkBusinessCardDetail props={props} />
    </ThemeProvider>
  );
};
